import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  const [flashcards, setFlashcards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [filterDomain, setFilterDomain] = useState('all');
  const [studyMode, setStudyMode] = useState('quiz'); // 'flashcard' or 'quiz'
  const [cardSet, setCardSet] = useState('v1'); // 'v1' or 'v2'

  useEffect(() => {
    const filename = cardSet === 'v1' ? '/flashcards.md' : '/flashcards-v2.md';
    fetch(filename)
      .then(response => response.text())
      .then(text => {
        const cards = parseFlashcards(text);
        setFlashcards(cards);
      });
  }, [cardSet]);

  const parseFlashcards = (text) => {
    const cards = [];
    const lines = text.split('\n');
    let currentCard = null;
    let currentDomain = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (line.startsWith('## Domain')) {
        currentDomain = line.replace('## ', '').replace(' - Additional Questions', '');
      } else if (line.startsWith('## Key Acronyms')) {
        currentDomain = 'Key Acronyms';
      } else if (line.startsWith('## Attack Indicators')) {
        currentDomain = 'Attack Indicators and Response';
      } else if (line.startsWith('Question:')) {
        if (currentCard) {
          cards.push(currentCard);
        }
        currentCard = {
          question: line.replace('Question: ', ''),
          options: [],
          domain: currentDomain
        };
      } else if (line.match(/^[A-D]\)/)) {
        if (currentCard) {
          currentCard.options.push(line);
        }
      } else if (line.startsWith('Answer:')) {
        if (currentCard) {
          currentCard.answer = line.replace('Answer: ', '');
          cards.push(currentCard);
          currentCard = null;
        }
      }
    }

    return cards;
  };

  const filteredCards = filterDomain === 'all'
    ? flashcards
    : flashcards.filter(card => card.domain.includes(filterDomain));

  const currentCard = filteredCards[currentIndex];

  const handleNext = () => {
    setShowAnswer(false);
    setSelectedAnswer(null);
    setCurrentIndex((prev) => (prev + 1) % filteredCards.length);
  };

  const handlePrevious = () => {
    setShowAnswer(false);
    setSelectedAnswer(null);
    setCurrentIndex((prev) => (prev - 1 + filteredCards.length) % filteredCards.length);
  };

  const handleAnswerSelect = (option) => {
    if (selectedAnswer) return; // Already answered

    setSelectedAnswer(option);
    const isCorrect = option.startsWith(currentCard.answer + ')');

    if (isCorrect) {
      setScore(prev => ({ correct: prev.correct + 1, total: prev.total + 1 }));
    } else {
      setScore(prev => ({ ...prev, total: prev.total + 1 }));
    }
  };

  const handleShuffle = () => {
    const shuffled = [...filteredCards].sort(() => Math.random() - 0.5);
    setFlashcards(prevCards => {
      const otherDomainCards = prevCards.filter(card =>
        filterDomain !== 'all' && !card.domain.includes(filterDomain)
      );
      return [...shuffled, ...otherDomainCards];
    });
    setCurrentIndex(0);
    setShowAnswer(false);
    setSelectedAnswer(null);
  };

  const resetScore = () => {
    setScore({ correct: 0, total: 0 });
    setSelectedAnswer(null);
    setShowAnswer(false);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, filteredCards.length]);

  if (!currentCard) {
    return <div className="App">Loading flashcards...</div>;
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>CompTIA Security+ SY0-701 Flashcards</h1>

        <div className="controls">
          <div className="mode-selector">
            <button
              className={studyMode === 'flashcard' ? 'active' : ''}
              onClick={() => { setStudyMode('flashcard'); setSelectedAnswer(null); }}
            >
              Flashcard Mode
            </button>
            <button
              className={studyMode === 'quiz' ? 'active' : ''}
              onClick={() => { setStudyMode('quiz'); setShowAnswer(false); }}
            >
              Quiz Mode
            </button>
          </div>

          <div className="mode-selector">
            <button
              className={cardSet === 'v1' ? 'active' : ''}
              onClick={() => { setCardSet('v1'); setCurrentIndex(0); setSelectedAnswer(null); setShowAnswer(false); }}
            >
              Original (87 questions)
            </button>
            <button
              className={cardSet === 'v2' ? 'active' : ''}
              onClick={() => { setCardSet('v2'); setCurrentIndex(0); setSelectedAnswer(null); setShowAnswer(false); }}
            >
              V2 (156 questions)
            </button>
          </div>

          <div className="filter-controls">
            <select
              value={filterDomain}
              onChange={(e) => { setFilterDomain(e.target.value); setCurrentIndex(0); }}
            >
              <option value="all">All Domains</option>
              <option value="Domain 1">Domain 1: General Security (12%)</option>
              <option value="Domain 2">Domain 2: Threats & Vulnerabilities (22%)</option>
              <option value="Domain 3">Domain 3: Security Architecture (18%)</option>
              <option value="Domain 4">Domain 4: Security Operations (28%)</option>
              <option value="Domain 5">Domain 5: Program Management (20%)</option>
              <option value="Key Acronyms">Key Acronyms</option>
              <option value="Attack Indicators">Attack Indicators</option>
            </select>
            <button onClick={handleShuffle}>🔀 Shuffle</button>
          </div>
        </div>

        <div className="progress">
          <p>{currentCard.domain}</p>
          <p>Card {currentIndex + 1} of {filteredCards.length}</p>
          {score.total > 0 && (
            <p className="score">
              Score: {score.correct}/{score.total} ({Math.round(score.correct / score.total * 100)}%)
              <button onClick={resetScore} className="reset-score">Reset</button>
            </p>
          )}
        </div>

        <div className="flashcard">
          <div className="question">
            <h2>{currentCard.question}</h2>
          </div>

          {studyMode === 'flashcard' ? (
            <div className="flashcard-mode">
              {!showAnswer ? (
                <button className="reveal-btn" onClick={() => setShowAnswer(true)}>
                  Show Answer
                </button>
              ) : (
                <div className="answer-section">
                  {currentCard.options.map((option, idx) => (
                    <div
                      key={idx}
                      className={`option ${option.startsWith(currentCard.answer + ')') ? 'correct-option' : ''}`}
                    >
                      {option}
                    </div>
                  ))}
                  <p className="correct-answer">
                    ✓ Correct Answer: {currentCard.answer}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="quiz-mode">
              {currentCard.options.map((option, idx) => {
                const isCorrect = option.startsWith(currentCard.answer + ')');
                const isSelected = selectedAnswer === option;

                let className = 'option';
                if (selectedAnswer) {
                  if (isSelected && isCorrect) className += ' correct';
                  else if (isSelected && !isCorrect) className += ' incorrect';
                  else if (isCorrect) className += ' correct-option';
                }

                return (
                  <button
                    key={idx}
                    className={className}
                    onClick={() => handleAnswerSelect(option)}
                    disabled={selectedAnswer !== null}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="navigation">
          <button onClick={handlePrevious}>← Previous</button>
          <button onClick={handleNext}>Next →</button>
        </div>

        <div className="keyboard-hint">
          <small>Tip: Use arrow keys ← → to navigate</small>
        </div>
      </header>
    </div>
  );
}

export default App;
